import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  ReactNode
} from 'react'
import {
  DraggableComponent,
  DroppableComponent,
  onDragEnd
} from '@components/common/DragAndDrop'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import {
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  SxProps,
  Theme
} from '@mui/material'
import useTableStyles from '@styles/table/table'
import {
  sortAlphaNum,
  sortDate,
  sortNumber,
  sortString
} from '@utils/sortTables'
import CommonPagination from '../Pagination'
import PlaceHolder from '../PlaceHolder'
import { DropResult } from 'react-beautiful-dnd'

// Define types for pagination component
interface CommonPaginationProps {
  enabled?: boolean
  queries?: Record<string, any>
  setQueries?: (queries: Record<string, any>) => void
  count: number
  page: number
  setPage: (page: number) => void
  rowsPerPage: number
  setRowsPerPage: (rowsPerPage: number) => void
}

// Define types for column configuration
export interface Column {
  field: string
  title: ReactNode
  width?: string | number
  align?: 'left' | 'center' | 'right'
  hidden?: boolean
  colSpan?: number
  cellStyle?: SxProps<Theme>
  headerStyle?: SxProps<Theme>
  clickable?: boolean
  sorting?: boolean
  sortBy?: string
  isAlphaNum?: boolean
  customSort?: (direction: string) => void
}

// Define type for data items
export interface DataItem {
  id: string | number
  className?: string
  clickable?: boolean
  hover?: boolean
  hidden?: boolean
  items?: DataItem[]
  draggable?: boolean
  isSubtotal?: boolean
  order: number
  [key: string]: any
}

// Type for the internal state of sort order
interface OrderState {
  direction: 'asc' | 'desc'
  counter: number
  isCustom?: boolean
}

// Props for the CollapsableRow component
interface CollapsableRowProps {
  datumItems?: DataItem[]
  columns: Column[]
  open: (string | number)[] | boolean
  dragAndDropEnabled?: boolean
  collapseEnabled?: boolean
  patchData?: (id: string, data: any) => Promise<any>
  mutateData?: () => Promise<any>
  lastRow?: boolean
  onChildRowClick?: (event: React.MouseEvent, item: DataItem) => void
  sortProps?: {
    order?: OrderState
    sortingEnabled?: boolean
  }
  tableStyle?: SxProps<Theme>
  selectionProps?: {
    selectionEnabled?: boolean
    itemsSelected?: (string | number)[]
    setItemsSelected?: (items: (string | number)[]) => void
    onSelectionChange?: (items: (string | number)[]) => void
  }
}

// Props for the RowTemplate component
interface RowTemplateProps {
  datum: DataItem
  index: number
  columns: Column[]
  dragAndDropEnabled?: boolean
  collapseProps?: {
    collapseEnabled?: boolean
    dragAndDropEnabled?: boolean
    patchData?: (id: string, data: any) => Promise<any>
    mutateData?: () => Promise<any>
    defaultOpen?: boolean | (string | number)[]
  }
  disableDrag?: boolean
  isNonDraggableRow?: boolean
  lastRow?: boolean
  open?: boolean
  setOpen: React.Dispatch<React.SetStateAction<(string | number)[]>>
  onRowClick?: (e: React.MouseEvent, datum: DataItem, column?: Column) => void
  onChildRowClick?: (e: React.MouseEvent, item: DataItem) => void
  sortProps?: {
    sortingEnabled?: boolean
    order?: OrderState
  }
  tableStyle?: SxProps<Theme>
  selectionProps?: {
    selectionEnabled?: boolean
    itemsSelected?: (string | number)[]
    setItemsSelected?: (items: (string | number)[]) => void
    onSelectionChange?: (items: (string | number)[]) => void
  }
}

// Props for the main TableTemplate component
export interface TableTemplateProps {
  headerProps?: {
    columns?: Column[]
    extraTableHead?: ReactNode
    extraTableHeadRow?: Column[]
    enabled?: boolean
  }
  bodyProps?: {
    data?: DataItem[] | null
    isLoading?: boolean
    onRowClick?: (e: React.MouseEvent, datum: DataItem, column?: Column) => void
    onChildRowClick?: (e: React.MouseEvent, item: DataItem) => void
  }
  footerProps?: {
    data?: DataItem[]
  }
  dragAndDropProps?: {
    patchData?: (id: string, data: any) => Promise<any>
    mutateData?: () => Promise<any>
    enabled?: boolean
    NonDraggableRows?: DataItem[]
  }
  collapseProps?: {
    collapseEnabled?: boolean
    dragAndDropEnabled?: boolean
    patchData?: (id: string, data: any) => Promise<any>
    mutateData?: () => Promise<any>
    defaultOpen?: boolean | (string | number)[]
  }
  placeholderProps?: {
    src?: string
    width?: string | number
    height?: string | number
    text?: string
    enabled?: boolean
    customPlaceholder?: ReactNode
    displayPlaceholder?: boolean
  }
  sortProps?: {
    enabled?: boolean
  }
  selectionProps?: {
    enabled?: boolean
    onSelectionChange?: (items: (string | number)[]) => void
    selectedItems?: (string | number)[]
  }
  paginationProps?: CommonPaginationProps
  tableContainerStyle?: SxProps<Theme>
  tableStyle?: SxProps<Theme>
}

const CollapsableRow: React.FC<CollapsableRowProps> = props => {
  const classesTable = useTableStyles()

  const {
    datumItems,
    columns,
    open,
    dragAndDropEnabled,
    collapseEnabled,
    patchData,
    mutateData,
    lastRow = false,
    onChildRowClick,
    sortProps = {},
    tableStyle = {},
    selectionProps = {}
  } = props

  const { order } = sortProps

  const {
    selectionEnabled,
    itemsSelected = [],
    setItemsSelected = () => {},
    onSelectionChange = () => {}
  } = selectionProps

  const [items, setItems] = useState<DataItem[]>([])

  useEffect(() => {
    setItems(
      datumItems
        ? [...datumItems]?.sort((a, b) => (a.order || 0) - (b.order || 0))
        : []
    )
  }, [datumItems, order])

  return collapseEnabled ? (
    <TableRow hover={false}>
      <TableCell
        colSpan={500}
        sx={{
          p: 0,
          borderBottom: t =>
            lastRow ? 0 : `1px solid ${t.palette.other.stroke}`
        }}
      >
        <Collapse in={Boolean(open)} timeout="auto" unmountOnExit>
          <Table aria-label="zones_table" sx={tableStyle}>
            <TableBody
              component={
                dragAndDropEnabled
                  ? DroppableComponent({
                      onDragEnd: (result: DropResult) =>
                        onDragEnd({
                          result,
                          items,
                          setItems: (updatedItems: DataItem[]) =>
                            setItems(updatedItems),
                          patchData,
                          mutateData
                        })
                    })
                  : 'tbody'
              }
            >
              {items.map((item, index) => (
                <TableRow
                  key={`${item.id}-${index}`}
                  className={classesTable[item.className ?? 'row']}
                  component={
                    dragAndDropEnabled
                      ? DraggableComponent({
                          draggableId: item.id.toString(),
                          index,
                          className: classesTable[item.className ?? 'row']
                        })
                      : 'tr'
                  }
                >
                  {columns.map(col => {
                    return col && !col.hidden ? (
                      <TableCell
                        key={`${col.field}-${item.id}`}
                        sx={{
                          width: col.width ?? 'fit-content',
                          ...col.cellStyle,
                          ...item[col.field]?.style
                        }}
                        align={col.align ?? 'left'}
                        colSpan={col.colSpan || item[col.field]?.colSpan || 1}
                      >
                        {item[col.field]?.label ?? item[col.field]}
                      </TableCell>
                    ) : undefined
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </TableCell>
    </TableRow>
  ) : (
    <>
      {items.map((item, index) => (
        <TableRow
          key={`${item.id}-${index}`}
          className={classesTable[item.className ?? 'row']}
          component={
            dragAndDropEnabled
              ? DraggableComponent({
                  draggableId: item.id.toString(),
                  index,
                  className: classesTable[item.className ?? 'row']
                })
              : 'tr'
          }
          onClick={onChildRowClick ? e => onChildRowClick(e, item) : undefined}
          hover={item.hover ?? true}
          sx={{
            position: 'relative',
            ...(onChildRowClick &&
              (item.clickable ?? true) && { cursor: 'pointer' })
          }}
        >
          {selectionEnabled && (
            <TableCell>
              <Checkbox
                size="small"
                checked={itemsSelected?.includes(item.id)}
                onChange={() => {
                  const selection = itemsSelected?.includes(item.id)
                    ? itemsSelected.filter(x => x != item.id)
                    : [...itemsSelected, item.id]
                  setItemsSelected(selection)
                  onSelectionChange(selection)
                }}
              />
            </TableCell>
          )}
          {columns.map(col =>
            col && !col.hidden && item[col.field] !== false ? (
              <TableCell
                key={`${col.field}-${item.id}`}
                sx={{
                  width: col.width ?? 'fit-content',
                  ...col.cellStyle,
                  ...item[col.field]?.style
                }}
                align={col.align ?? 'left'}
                colSpan={col.colSpan || item[col.field]?.colSpan || 1}
              >
                {item[col.field]?.label ?? item[col.field]}
              </TableCell>
            ) : undefined
          )}
        </TableRow>
      ))}
    </>
  )
}

const RowTemplate: React.FC<RowTemplateProps> = props => {
  const classesTable = useTableStyles()

  const {
    datum,
    index,
    columns,
    dragAndDropEnabled,
    collapseProps = {},
    disableDrag,
    isNonDraggableRow = false,
    lastRow = false,
    open,
    setOpen,
    selectionProps = {},
    onRowClick,
    onChildRowClick,
    sortProps,
    tableStyle
  } = props

  const {
    collapseEnabled = false,
    dragAndDropEnabled: collapseDragAndDropEnabled = false,
    patchData: collapsePatchData = () => Promise.resolve(null),
    mutateData: collapseMutateData = () => Promise.resolve(null)
  } = collapseProps

  const {
    selectionEnabled,
    itemsSelected = [],
    setItemsSelected = () => {},
    onSelectionChange = () => {}
  } = selectionProps

  const wholeRowClickable = !columns?.filter(col => col.clickable === true)
    ?.length
  const rowClickable = onRowClick && (datum?.clickable ?? true)

  const itemsIdsList = datum?.items?.map(item => item.id) || []
  const allChildItemsSelected = itemsIdsList?.every(id =>
    itemsSelected?.includes(id)
  )
  const someChildItemsSelected = itemsIdsList?.some(id =>
    itemsSelected?.includes(id)
  )

  return (
    <Fragment>
      {!datum.hidden && (
        <TableRow
          className={classesTable[datum.className ?? 'row']}
          component={
            dragAndDropEnabled
              ? DraggableComponent({
                  draggableId: datum.id.toString(),
                  index,
                  className: classesTable[datum.className ?? 'row'],
                  dragHandleDisabled: disableDrag,
                  dragHandleVisible: datum.draggable ?? true,
                  style: { ...(rowClickable && { cursor: 'pointer' }) },
                  onRowClick:
                    onRowClick && !selectionEnabled && wholeRowClickable
                      ? () => onRowClick(new MouseEvent('click') as any, datum)
                      : undefined
                })
              : 'tr'
          }
          hover={datum.hover ?? false}
          onClick={
            onRowClick && !selectionEnabled && wholeRowClickable
              ? e => onRowClick(e, datum)
              : undefined
          }
          sx={{ ...(rowClickable && { cursor: 'pointer' }) }}
        >
          {isNonDraggableRow && <TableCell />}
          {selectionEnabled && (
            <TableCell>
              <Checkbox
                size="small"
                checked={
                  datum.items
                    ? allChildItemsSelected
                    : itemsSelected?.includes(datum.id)
                }
                {...(datum.items?.length > 0 && {
                  indeterminate:
                    someChildItemsSelected && !allChildItemsSelected
                })}
                onChange={() => {
                  let selection: (string | number)[] = []
                  if (!datum.items) {
                    selection = itemsSelected?.includes(datum.id)
                      ? itemsSelected.filter(x => x != datum.id)
                      : [...(itemsSelected || []), datum.id]
                    setItemsSelected(selection)
                  } else {
                    selection = allChildItemsSelected
                      ? itemsSelected.filter(x => !itemsIdsList?.includes(x))
                      : [...(itemsSelected || []), ...itemsIdsList]
                    setItemsSelected(selection)
                  }

                  onSelectionChange(selection)
                }}
              />
            </TableCell>
          )}
          {columns.map((col, colIndex) =>
            col && !col.hidden && datum[col.field] !== false ? (
              <TableCell
                key={`${col.field}-${datum.id}`}
                id={`tablecell-${col.field}-${datum.id}`}
                sx={{
                  width: col.width ?? 'fit-content',
                  ...col.cellStyle,
                  ...datum[col.field]?.style,
                  ...(col.clickable &&
                    !rowClickable && {
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': { color: 'primary.main', fontWeight: 600 }
                    })
                }}
                align={col.align ?? 'left'}
                colSpan={col.colSpan || datum[col.field]?.colSpan || 1}
                onClick={
                  onRowClick &&
                  ((wholeRowClickable && selectionEnabled) ||
                    (!wholeRowClickable &&
                      col.clickable === true &&
                      rowClickable))
                    ? e => onRowClick(e, datum)
                    : onRowClick &&
                      ((wholeRowClickable && selectionEnabled) ||
                        (!wholeRowClickable &&
                          col.clickable === true &&
                          !rowClickable))
                    ? e => onRowClick(e, datum, col)
                    : undefined
                }
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  width="100%"
                  justifyContent={
                    col.align === 'right'
                      ? 'flex-end'
                      : col.align === 'center'
                      ? 'center'
                      : 'flex-start'
                  }
                >
                  {collapseEnabled && colIndex === 0 && !datum?.isSubtotal && (
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => {
                        setOpen(prev =>
                          prev?.includes(datum.id)
                            ? prev.filter(p => p !== datum.id)
                            : [...prev, datum.id]
                        )
                      }}
                      data-testid="IconButton-fc5c4dfa-5840-42d0-a161-42eaff38181b"
                    >
                      {open ? (
                        <KeyboardArrowUp color="secondary" fontSize="small" />
                      ) : (
                        <KeyboardArrowDown color="secondary" fontSize="small" />
                      )}
                    </IconButton>
                  )}
                  <div style={{ width: '100%' }}>
                    {datum[col.field]?.label ?? datum[col.field]}
                  </div>
                </Stack>
              </TableCell>
            ) : undefined
          )}
        </TableRow>
      )}
      {datum.items && datum.items.length > 0 && (
        <CollapsableRow
          datumItems={datum.items}
          columns={columns}
          open={open ? true : false}
          collapseEnabled={collapseEnabled}
          dragAndDropEnabled={collapseDragAndDropEnabled}
          patchData={collapsePatchData}
          mutateData={collapseMutateData}
          lastRow={lastRow}
          onChildRowClick={onChildRowClick}
          sortProps={sortProps}
          tableStyle={tableStyle}
          selectionProps={selectionProps}
        />
      )}
    </Fragment>
  )
}

const TableTemplate = React.forwardRef<HTMLDivElement, TableTemplateProps>(
  (props, ref) => {
    const classesTable = useTableStyles()

    const {
      headerProps = {},
      bodyProps = {},
      footerProps = {},
      dragAndDropProps = {},
      collapseProps = {},
      placeholderProps = {},
      sortProps = {},
      selectionProps = {},
      paginationProps = {},
      tableContainerStyle = {},
      tableStyle = {}
    } = props

    const {
      columns = [],
      extraTableHead = null,
      extraTableHeadRow = null,
      enabled: headerEnabled = true
    } = headerProps
    const {
      data = null,
      isLoading = false,
      onRowClick = null,
      onChildRowClick = null
    } = bodyProps
    const { data: footerData = [] } = footerProps
    const {
      patchData = () => Promise.resolve(null),
      mutateData = () => Promise.resolve(null),
      enabled: dragAndDropEnabled = false,
      NonDraggableRows = []
    } = dragAndDropProps

    const {
      src,
      width,
      height,
      text: placeholderText,
      enabled: placeholderEnabled = true,
      customPlaceholder = null,
      displayPlaceholder = false
    } = placeholderProps || {}

    const { defaultOpen } = collapseProps || {}

    const { enabled: sortingEnabled = false } = sortProps

    const {
      enabled: selectionEnabled = false,
      onSelectionChange = () => {},
      selectedItems = []
    } = selectionProps

    const { enabled: paginationEnabled = false } = paginationProps || {
      enabled: false
    }

    const [items, setItems] = useState<DataItem[] | null>(null)
    const [backupItems, setBackupItems] = useState<DataItem[] | null>(null)
    const [disableDrag, setDisableDrag] = useState<boolean>(false)
    const [open, setOpen] = useState<(string | number)[]>([])
    const [orderBy, setOrderBy] = useState<string>('')
    const [order, setOrder] = useState<OrderState>({
      direction: 'asc',
      counter: 0
    })

    const [itemsSelected, setItemsSelected] =
      useState<(string | number)[]>(selectedItems)

    const sortingFunction = useCallback(
      (
        data: DataItem[],
        sortBy: string,
        direction: 'asc' | 'desc',
        isAlphaNum = false,
        objectField = false
      ) => {
        const itemsCopy = [...data]
        itemsCopy.sort((a, b) => {
          let [el1, el2] = direction === 'asc' ? [a, b] : [b, a]
          if (objectField) [el1, el2] = direction === 'asc' ? [a, b] : [b, a]
          if (isAlphaNum) return sortAlphaNum(el1, el2, sortBy)
          else if (typeof el1[sortBy] == 'number')
            return sortNumber(el1, el2, sortBy)
          else if (typeof el1[sortBy] == 'string')
            return sortString(el1, el2, sortBy)
          else return sortDate(el1, el2, sortBy)
        })
        setItems(itemsCopy)
      },
      []
    )

    useEffect(() => {
      if (!isLoading && data) {
        if (orderBy && order && !order.isCustom)
          sortingFunction(data, orderBy, order.direction)
        else setItems(data)
        setBackupItems(data)
        if (!selectedItems?.length) {
          setItemsSelected([])
        }
      } else if (!isLoading && !data) {
        setItems([])
        setBackupItems([])
      }
    }, [
      data,
      isLoading,
      setItems,
      setBackupItems,
      selectedItems?.length,
      order,
      orderBy,
      sortingFunction
    ])

    useEffect(() => {
      if (items && defaultOpen === true) {
        const rowsIds = items.map(item => item.id)
        const nonDraggableRowsIds = NonDraggableRows?.map(item => item.id) || []
        setOpen([...rowsIds, ...nonDraggableRowsIds])
      } else if (Array.isArray(defaultOpen))
        setOpen(prev => [...prev, ...defaultOpen])
    }, [defaultOpen, items, setOpen, NonDraggableRows])

    useEffect(() => {
      if (defaultOpen === false) setOpen([])
      else if (Array.isArray(defaultOpen))
        setOpen(prev => [...prev, ...defaultOpen])
    }, [defaultOpen])

    const sortingChildrenFunction = (
      data: DataItem[],
      sortBy: string,
      direction: 'asc' | 'desc',
      isAlphaNum = false
    ) => {
      const itemsCopy = [...data]
      itemsCopy.forEach(item => {
        item.items?.sort((a, b) => {
          const [el1, el2] = direction === 'asc' ? [a, b] : [b, a]
          if (isAlphaNum) return sortAlphaNum(el1, el2, sortBy)
          else if (typeof el1[sortBy] == 'number')
            return sortNumber(el1, el2, sortBy)
          else if (typeof el1[sortBy] == 'string')
            return sortString(el1, el2, sortBy)
          else return sortDate(el1, el2, sortBy)
        })
      })
      setItems(itemsCopy)
    }

    const sortItems = useCallback(
      (col: Column) => {
        if (items) {
          const sortBy = col.sortBy || col.field
          const direction = order.direction === 'asc' ? 'desc' : 'asc'
          const counter = order.counter

          const itemsHaveChildren =
            items.filter(item => item.items?.length > 0)?.length > 0

          if (orderBy && sortBy != orderBy) {
            setOrder({ direction: 'asc', counter: 0 })
            setOrderBy('')
            if (!col.customSort) setItems(backupItems)
            return
          }

          if (col.customSort) {
            if (counter >= 2) {
              col.customSort('')
              setOrder({ direction: 'asc', counter: 0 })
              setOrderBy('')
              return
            }
            col.customSort(direction)
          } else {
            if (counter >= 2) {
              setItems(backupItems)
              setOrder({ direction: 'asc', counter: 0 })
              setOrderBy('')
              return
            }
            if (itemsHaveChildren)
              sortingChildrenFunction(items, sortBy, direction, col.isAlphaNum)
            else sortingFunction(items, sortBy, direction, col.isAlphaNum)
          }
          setOrder(prev => ({
            direction,
            counter: prev.counter + 1,
            isCustom: !!col?.customSort
          }))
          setOrderBy(sortBy)
        }
      },
      [
        order,
        setOrder,
        setOrderBy,
        items,
        setItems,
        backupItems,
        sortingFunction,
        orderBy
      ]
    )

    const itemsHaveChildren = items?.some(item => item.items)
    const itemsLength = itemsHaveChildren
      ? items?.flatMap(item => item.items || []).length
      : items?.length
    const allItemsSelected = itemsSelected?.length === itemsLength

    const dragHandleDisabled = Boolean(orderBy)

    return (
      <TableContainer
        className={classesTable.container}
        sx={tableContainerStyle}
        ref={ref}
      >
        <Table aria-label="table-template" sx={tableStyle}>
          {extraTableHead && (
            <TableHead>
              <TableRow>
                <TableCell colSpan={100}>{extraTableHead}</TableCell>
              </TableRow>
            </TableHead>
          )}
          {headerEnabled && items && items?.length > 0 && (
            <TableHead>
              {extraTableHeadRow && (
                <TableRow>
                  {dragAndDropEnabled && <TableCell />}
                  {selectionEnabled && <TableCell />}
                  {extraTableHeadRow.map(col =>
                    col && !col.hidden ? (
                      <TableCell
                        key={col.field}
                        sx={{
                          width: col.width ?? 'fit-content',
                          ...col.headerStyle
                        }}
                        align={col.align ?? 'left'}
                        colSpan={col.colSpan || 1}
                        className={classesTable.headerCell}
                      >
                        {col.title}
                      </TableCell>
                    ) : undefined
                  )}
                </TableRow>
              )}
              <TableRow hover={false}>
                {dragAndDropEnabled && <TableCell />}
                {selectionEnabled && (
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={allItemsSelected}
                      indeterminate={
                        itemsSelected?.length > 0 &&
                        itemsSelected?.length < (itemsLength || 0)
                      }
                      onChange={() => {
                        const selection = allItemsSelected
                          ? []
                          : itemsHaveChildren && items
                          ? items?.flatMap(item =>
                              (item.items || []).map(i => i.id)
                            )
                          : items?.map(i => i.id) || []
                        setItemsSelected(selection)
                        onSelectionChange(selection)
                      }}
                    />
                  </TableCell>
                )}
                {columns.map(col =>
                  col && !col.hidden ? (
                    <TableCell
                      key={col.field}
                      sx={{
                        width: col.width ?? 'fit-content',
                        ...col.headerStyle
                      }}
                      align={col.align ?? 'left'}
                      colSpan={col.colSpan || 1}
                      className={classesTable.headerCell}
                    >
                      {sortingEnabled &&
                      (col.sorting === true || col.sorting === undefined) ? (
                        <TableSortLabel
                          active={orderBy === (col.sortBy || col.field)}
                          direction={
                            orderBy === (col.sortBy || col.field)
                              ? order.direction
                              : 'asc'
                          }
                          onClick={() => sortItems(col)}
                        >
                          {col.title}
                        </TableSortLabel>
                      ) : (
                        col.title
                      )}
                    </TableCell>
                  ) : undefined
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody
            component={
              dragAndDropEnabled && items
                ? DroppableComponent({
                    onDragEnd: (result: DropResult) =>
                      onDragEnd({
                        result,
                        items: items || [],
                        setItems: (updatedItems: DataItem[]) =>
                          setItems(updatedItems),
                        patchData,
                        mutateData,
                        setDisableDrag
                      })
                  })
                : 'tbody'
            }
          >
            {(isLoading || !items) && (
              <TableRow hover={false} className={classesTable.row}>
                <TableCell align="center" colSpan={100} sx={{ height: 300 }}>
                  <CircularProgress color="secondary" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              items &&
              (!items?.length || displayPlaceholder) &&
              placeholderEnabled && (
                <TableRow hover={false} className={classesTable.row}>
                  <TableCell align="center" colSpan={100}>
                    {!customPlaceholder ? (
                      <PlaceHolder
                        src={src}
                        width={width}
                        height={height}
                        wordingBordered={placeholderText}
                      />
                    ) : (
                      customPlaceholder
                    )}
                  </TableCell>
                </TableRow>
              )}
            {!isLoading &&
              items &&
              items.length > 0 &&
              items.map((datum, index) => {
                return (
                  <RowTemplate
                    key={`${datum.id}-${index}`}
                    datum={datum}
                    index={index}
                    columns={columns}
                    dragAndDropEnabled={dragAndDropEnabled}
                    collapseProps={collapseProps}
                    disableDrag={disableDrag || dragHandleDisabled}
                    lastRow={
                      index === (data?.length || 0) - 1 &&
                      !NonDraggableRows?.length
                    }
                    onRowClick={onRowClick}
                    onChildRowClick={onChildRowClick}
                    open={open?.includes(datum.id)}
                    setOpen={setOpen}
                    selectionProps={{
                      selectionEnabled,
                      itemsSelected,
                      setItemsSelected,
                      onSelectionChange
                    }}
                    sortProps={{ sortingEnabled, order }}
                    tableStyle={tableStyle}
                  />
                )
              })}
          </TableBody>
          {!isLoading && NonDraggableRows?.length > 0 && (
            <TableBody>
              {NonDraggableRows?.map((datum, index) => (
                <RowTemplate
                  key={`${datum.id}-${index}`}
                  datum={datum}
                  index={index}
                  columns={columns}
                  dragAndDropEnabled={false}
                  collapseProps={collapseProps}
                  disableDrag={disableDrag}
                  isNonDraggableRow
                  lastRow={true}
                  open={open?.includes(datum.id)}
                  setOpen={setOpen}
                  selectionProps={{
                    selectionEnabled,
                    itemsSelected,
                    setItemsSelected,
                    onSelectionChange
                  }}
                />
              ))}
            </TableBody>
          )}
          {!isLoading &&
            items &&
            items.length > 0 &&
            (footerData.length > 0 || paginationEnabled) && (
              <TableFooter className={classesTable.footer}>
                {footerData.map(footer =>
                  footer ? (
                    <TableRow
                      key={footer.id}
                      className={classesTable[footer.className ?? 'rowTotal']}
                      hover={false}
                    >
                      {dragAndDropEnabled && <TableCell />}
                      {selectionEnabled && <TableCell />}
                      {columns.map((col, colIndex) =>
                        col && !col.hidden ? (
                          <TableCell
                            key={`total-${col.field}-${colIndex}`}
                            sx={{
                              width: col.width ?? 'fit-content',
                              ...col.cellStyle,
                              ...footer[col.field]?.style,
                              fontWeight: 'bold'
                            }}
                            align={col.align ?? 'left'}
                            colSpan={
                              col.colSpan || footer[col.field]?.colSpan || 1
                            }
                          >
                            {footer[col.field]?.label ?? footer[col.field]}
                          </TableCell>
                        ) : undefined
                      )}
                    </TableRow>
                  ) : undefined
                )}
                {paginationEnabled &&
                  paginationProps &&
                  'page' in paginationProps &&
                  'rowsPerPage' in paginationProps &&
                  'count' in paginationProps &&
                  'setPage' in paginationProps &&
                  'setRowsPerPage' in paginationProps && (
                    <CommonPagination
                      {...(paginationProps as CommonPaginationProps)}
                    />
                  )}
              </TableFooter>
            )}
        </Table>
      </TableContainer>
    )
  }
)

export default TableTemplate
